import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Clock, Users, ChevronRight } from 'lucide-react';
import { scenarios } from '../data/scenarios';
import { useClass } from '../context/ClassContext';
import { NiveauFilter } from '../types';

export default function ScenarioList() {
  const [niveauFilter, setNiveauFilter] = React.useState<NiveauFilter>('Tous');
  const navigate = useNavigate();
  const { currentClass } = useClass();

  // Filtrer les scénarios selon le niveau et les assignations
  const filteredScenarios = scenarios.filter(scenario => {
    const matchesFilter = niveauFilter === 'Tous' || scenario.niveau === niveauFilter;
    const isAssigned = currentClass?.students.some(
      student => student.assignedScenario === scenario.id
    );
    return currentClass?.students.length ? (isAssigned && matchesFilter) : matchesFilter;
  });

  const getNiveauColor = (niveau: string) => {
    switch (niveau) {
      case 'Débutant':
        return 'bg-green-100 text-green-800';
      case 'Intermédiaire':
        return 'bg-yellow-100 text-yellow-800';
      case 'Avancé':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Obtenir les élèves assignés à un scénario
  const getAssignedStudents = (scenarioId: string) => {
    if (!currentClass) return [];
    return currentClass.students.filter(
      student => student.assignedScenario === scenarioId
    );
  };

  return (
    <section>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {currentClass?.students.length
            ? 'Scénarios assignés'
            : 'Tous les scénarios'
          }
        </h2>
        
        <div className="flex items-center space-x-2">
          <Filter className="h-5 w-5 text-gray-500" />
          <select
            value={niveauFilter}
            onChange={(e) => setNiveauFilter(e.target.value as NiveauFilter)}
            className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            <option value="Tous">Tous les niveaux</option>
            <option value="Débutant">Débutant</option>
            <option value="Intermédiaire">Intermédiaire</option>
            <option value="Avancé">Avancé</option>
          </select>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredScenarios.map((scenario) => {
            const assignedStudents = getAssignedStudents(scenario.id);
            
            return (
              <li key={scenario.id}>
                <div 
                  className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" 
                  onClick={() => navigate(`/scenario/${scenario.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {scenario.titre}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNiveauColor(scenario.niveau)}`}>
                          {scenario.niveau}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">{scenario.description}</p>
                      
                      {assignedStudents.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-blue-600">
                            Assigné à : {assignedStudents.map(s => 
                              `${s.firstName} ${s.lastName}`
                            ).join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        {scenario.duree} min
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Users className="h-4 w-4 mr-1" />
                        {scenario.participants} pers.
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}